import { itemsApi } from "#/api/itemsApi";
import InventoryMap from "#/components/ui/InventoryMap";
import { type Item } from "#/types/items";
import type { MapCountItem } from "#/types/stockAlert";
import { sanitize } from "#/utils/StringUtils";
import { Divider, Skeleton, Title, UnstyledButton } from "@mantine/core";
import { IconMapPinFilled } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const StocksAlert = () => {
  const { t } = useTranslation();
  const [selectedMapItem, setSelectedMapItem] = useState<MapCountItem>();
  const [allItems, setAllItems] = useState<Item[]>([]);

  const { data = [], isFetching } = useQuery({
    queryKey: ["items-map-data"],
    queryFn: async () => {
      const response = await itemsApi.getMapData();

      if (!response.data) {
        throw new Error("Failed to fetch map data");
      }

      const colorMap = new Map<string, string>();
      const usedColors = new Set<string>();

      const getNextAvailableColor = () => {
        const mantineColors = [
          "#228be6", // blue
          "#20c997", // teal
          "#845ef7", // violet
          "#e64980", // pink
          "#fab005", // yellow
          "#40c057", // green
          "#fa5252", // red
          "#15aabf", // cyan
          "#fd7e14", // orange
          "#1c7ed6", // darker blue
          "#82c91e", // lime
          "#f76707", // deep orange
        ];
        for (const color of mantineColors) {
          if (!usedColors.has(color)) {
            usedColors.add(color);
            return color;
          }
        }
        const hue = Math.floor(Math.random() * 360);
        return `hsl(${hue}, 70%, 50%)`;
      };

      const mapCountItems: MapCountItem[] = Object.entries(
        response.data?.counts || {}
      ).map(([key, count]) => {
        const items =
          response.data.items?.filter((item) => item.status === key) ?? [];
        const color = colorMap.get(key) ?? getNextAvailableColor();
        if (!colorMap.has(key)) {
          colorMap.set(key, color);
        }

        return {
          items,
          color,
          count,
          label: sanitize(key),
          status: key,
        };
      });

      setAllItems(response.data.items);

      // setSelectedMapItem(mapCountItems[0]);
      return mapCountItems;
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  return (
    <div className="w-full flex flex-col gap-2">
      <Title order={4}>{t("dashboard.stockAlerts.title")}</Title>
      {isFetching ? (
        <div className="w-full h-76 flex ">
          <Skeleton radius={"md"} />
        </div>
      ) : (
        <div className="w-full flex flex-col gap-4">
          <div className="w-full grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {data?.map((item) => (
              <UnstyledButton
                key={item.status}
                onClick={() => {
                  if (selectedMapItem?.status === item.status) {
                    setSelectedMapItem(undefined);
                  } else {
                    setSelectedMapItem(item);
                  }
                }}
              >
                <div
                  className={`w-full flex items-center gap-2 px-4 py-2 rounded-md
                    hover:bg-client-color/50 ${selectedMapItem?.status === item.status ? "bg-client-color/30" : "bg-client-color/10"}
                    transition-all duration-200 cursor-pointer`}
                >
                  <IconMapPinFilled color={item.color} size={16} />
                  <span className="text-sm font-medium capitalize">
                    {item.label}
                  </span>
                  <span className="text-lg font-bold ml-auto">
                    {item.count ?? "--"}
                  </span>
                </div>
              </UnstyledButton>
            ))}
          </div>
          <div className="w-full h-[350px] flex rounded-lg overflow-hidden">
            <InventoryMap
              markers={
                (selectedMapItem ? selectedMapItem.items : allItems).map(
                  (item) => {
                    const keyValueView = (
                      key: string,
                      value: string | number
                    ) => {
                      return (
                        <div className="flex items-center justify-between gap-1 text-xs">
                          <span className="">{key}:</span>
                          <span>{value}</span>
                        </div>
                      );
                    };
                    return {
                      longitude: item.longitude ?? item.site?.longitude,
                      latitude: item.latitude ?? item.site?.latitude,
                      color:
                        data.find((i) => i.status === item.status)?.color ?? "",
                      infoWindowContent: () => {
                        return (
                          <div className="min-w-60 w-fit flex flex-col gap-2 bg-white/70 backdrop-blur-sm rounded-md p-4">
                            <Title order={6}>{item.tag_id}</Title>
                            <div className="w-full flex flex-col gap-1">
                              {keyValueView(
                                t("dashboard.stockAlerts.circulationDate"),
                                item.date_of_circulation
                              )}
                              {keyValueView(
                                t("dashboard.stockAlerts.nextMaintenanceDate"),
                                item.next_maintenance_date
                              )}
                              {keyValueView(
                                t("dashboard.stockAlerts.lastMaintenanceDate"),
                                item.last_maintenance_date ?? "--"
                              )}
                              {keyValueView(
                                t("dashboard.stockAlerts.notifyEmail"),
                                item.notify_email ?? "--"
                              )}
                              {keyValueView(t("dashboard.stockAlerts.location"), item.location ?? "--")}
                              <Divider color="gray.5" className="my-2" />
                              <span>{`${item.longitude},     ${item.latitude}`}</span>
                            </div>
                          </div>
                        );
                      },
                    };
                  }
                ) ?? []
              }
              zoom={11}
              additionalComponent={null}
              options={{
                zoomControl: false,
                fullscreenControl: false,
                keyboardShortcuts: false,
              }}
            />
          </div>


        </div>
      )}
    </div>
  );
};

export default StocksAlert;
